export interface BaseQuestion {
  id?: string;
  label: string;
  text: string;
  imageUrl?: string;
  imageAlt?: string;
  imageCaption?: string;
  imageSpoilsAnswer?: boolean;
  explanation: string;
}

export interface ChoiceQuestion extends BaseQuestion {
  type: 'choice';
  choices: string[];
  options?: string[];
  correct: number;
}

export interface OrderQuestion extends BaseQuestion {
  type: 'order';
  answer: string;
  hint: string;
}

export interface MatchPair {
  left: string;
  right: string;
  image?: string;
  author?: string;
}

export interface MatchQuestion extends BaseQuestion {
  type: 'match';
  rightTitle?: string;
  pairs: MatchPair[];
}

export type TowerQuestion = ChoiceQuestion | OrderQuestion | MatchQuestion;

export const TOWER_QUESTIONS: TowerQuestion[] = [
  {
    id: 'q1',
    type: 'choice',
    label: 'TRẮC NGHIỆM · TẦNG 1',
    text: 'Tác phẩm “Gióng” của danh họa Nguyễn Tư Nghiêm được sáng tác bằng chất liệu truyền thống nào?',
    choices: ['Sơn mài', 'Sơn dầu', 'Tranh lụa', 'Khắc gỗ'],
    correct: 0,
    imageUrl: '/images/artifacts/giong-nguyen-tu-nghiem.webp',
    imageAlt: 'Tác phẩm Gióng của Nguyễn Tư Nghiêm',
    imageCaption: 'Gióng · Nguyễn Tư Nghiêm (Bảo vật Quốc gia, Sơn mài 1990)',
    imageSpoilsAnswer: false,
    explanation: '“Gióng” là một trong những đỉnh cao của chất liệu sơn mài hiện đại Việt Nam, kết hợp tinh hoa hoa văn Đông Sơn với ngôn ngữ tạo hình lập thể độc đáo.'
  },
  {
    id: 'q2',
    type: 'order',
    label: 'VUA TIẾNG VIỆT · TẦNG 2',
    text: 'Sắp xếp các chữ cái để tạo thành chất liệu đặc trưng của mỹ thuật Việt Nam:',
    hint: 'Chất liệu làm nên kiệt tác Bình Phong và Gióng',
    answer: 'SƠN MÀI',
    imageUrl: '/images/artifacts/binh-phong-nguyen-gia-tri.webp',
    imageAlt: 'Bình phong Nguyễn Gia Trí',
    imageCaption: 'Bình phong · Nguyễn Gia Trí (Bảo vật Quốc gia, Sơn mài 1939-1944)',
    imageSpoilsAnswer: true,
    explanation: 'Sơn mài là chất liệu hội họa truyền thống độc đáo của Việt Nam, đòi hỏi nhiều công đoạn mài và ủ sơn kỳ công trong bóng tối.'
  },
  {
    id: 'q3',
    type: 'match',
    label: 'NỐI TÁC PHẨM & TÁC GIẢ · TẦNG 3',
    text: 'Chọn một tác phẩm ở bên trái rồi nối với danh họa tương ứng ở bên phải:',
    rightTitle: 'TÁC GIẢ',
    imageUrl: '/images/artifacts/em-thuy-tran-van-can.webp',
    imageCaption: 'Các danh họa tiêu biểu trường Mỹ thuật Đông Dương',
    imageSpoilsAnswer: false,
    pairs: [
      { left: 'Em Thúy', right: 'Trần Văn Cẩn', image: '/images/artifacts/em-thuy-tran-van-can.webp' },
      { left: 'Gióng', right: 'Nguyễn Tư Nghiêm', image: '/images/artifacts/giong-nguyen-tu-nghiem.webp' },
      { left: 'Bình phong', right: 'Nguyễn Gia Trí', image: '/images/artifacts/binh-phong-nguyen-gia-tri.webp' }
    ],
    explanation: 'Đây đều là những đại danh họa của nền mỹ thuật hiện đại Việt Nam thuộc thế hệ trường Mỹ thuật Đông Dương lừng lẫy.'
  },
  {
    id: 'q4',
    type: 'choice',
    label: 'TRẮC NGHIỆM · TẦNG 4',
    text: 'Kiệt tác chân dung “Em Thúy” được danh họa Trần Văn Cẩn sáng tác vào năm nào?',
    choices: ['1943', '1954', '1930', '1975'],
    correct: 0,
    imageUrl: '/images/artifacts/em-thuy-tran-van-can.webp',
    imageAlt: 'Em Thúy của Trần Văn Cẩn',
    imageCaption: 'Em Thúy · Trần Văn Cẩn (Bảo vật Quốc gia, Sơn dầu, 1943)',
    imageSpoilsAnswer: true,
    explanation: 'Tác phẩm được vẽ vào năm 1943 bằng sơn dầu, mô tả cô cháu gái 8 tuổi ngồi trên chiếc ghế mây mộc mạc với đôi mắt trong veo lay động.'
  },
  {
    id: 'q5',
    type: 'order',
    label: 'VUA TIẾNG VIỆT · TẦNG 5',
    text: 'Sắp xếp các chữ cái để tìm tên tác phẩm kiệt tác chân dung của Trần Văn Cẩn:',
    hint: 'Bức tranh cô bé ngồi trên ghế mây mộc mạc',
    answer: 'EM THÚY',
    imageUrl: '/images/artifacts/em-thuy-tran-van-can.webp',
    imageCaption: 'Bảo vật Quốc gia · Em Thúy (Trần Văn Cẩn, 1943)',
    imageSpoilsAnswer: true,
    explanation: '“Em Thúy” được công nhận là Bảo vật Quốc gia và là một trong những bức chân dung xuất sắc nhất của thế kỷ 20.'
  },
  {
    id: 'q6',
    type: 'choice',
    label: 'TRẮC NGHIỆM · TẦNG 6',
    text: 'Kiệt tác “Kết nạp Đảng ở Điện Biên Phủ” của danh họa Nguyễn Sáng được sáng tác bằng chất liệu gì?',
    choices: ['Sơn mài', 'Tranh lụa', 'Sơn dầu', 'Bột màu'],
    correct: 0,
    imageUrl: '/images/artifacts/ket-nap-dang-o-dien-bien-phu-nguyen-sang.webp',
    imageAlt: 'Kết nạp Đảng ở Điện Biên Phủ của Nguyễn Sáng',
    imageCaption: 'Kết nạp Đảng ở Điện Biên Phủ · Nguyễn Sáng (Bảo vật Quốc gia, Sơn mài 1956)',
    imageSpoilsAnswer: false,
    explanation: 'Tác phẩm sử dụng nghệ thuật sơn mài đanh thép, tái hiện khoảnh khắc thiêng liêng ngay giữa chiến hào trong khói lửa Điện Biên Phủ năm 1954.'
  },
  {
    id: 'q7',
    type: 'order',
    label: 'VUA TIẾNG VIỆT · TẦNG 7',
    text: 'Sắp xếp các chữ cái để tìm địa danh lịch sử trong kiệt tác của Nguyễn Sáng:',
    hint: 'Chiến trường lừng lẫy năm châu, chấn động địa cầu năm 1954',
    answer: 'ĐIỆN BIÊN',
    imageUrl: '/images/artifacts/ket-nap-dang-o-dien-bien-phu-nguyen-sang.webp',
    imageAlt: 'Kết nạp Đảng ở Điện Biên Phủ',
    imageCaption: 'Bảo vật Quốc gia · Kết nạp Đảng ở Điện Biên Phủ (1956)',
    imageSpoilsAnswer: true,
    explanation: 'Chiến dịch Điện Biên Phủ lịch sử là nguồn cảm hứng vô tận cho các nghệ sĩ sáng tạo nên những kiệt tác bất hủ của dân tộc.'
  },
  {
    id: 'q8',
    type: 'choice',
    label: 'TRẮC NGHIỆM · TẦNG 8',
    text: 'Trong tác phẩm “Hai thiếu nữ và em bé” của Tô Ngọc Vân, hai thiếu nữ mặc trang phục truyền thống nào?',
    choices: ['Áo dài', 'Áo tứ thân', 'Áo bà ba', 'Áo yếm'],
    correct: 0,
    imageUrl: '/images/artifacts/hai-thieu-nu-va-em-be-to-ngoc-van.webp',
    imageAlt: 'Hai thiếu nữ và em bé của Tô Ngọc Vân',
    imageCaption: 'Hai thiếu nữ và em bé · Tô Ngọc Vân (Bảo vật Quốc gia, Sơn dầu 1944)',
    imageSpoilsAnswer: false,
    explanation: 'Bức tranh mô tả nét đẹp nền nã, tao nhã của phụ nữ Hà thành trong tà áo dài truyền thống thập niên 1940 với bố cục tam giác cổ điển hài hòa.'
  },
  {
    id: 'q9',
    type: 'order',
    label: 'VUA TIẾNG VIỆT · TẦNG 9',
    text: 'Sắp xếp các chữ cái để tìm dòng tranh dân gian in ván khắc gỗ trên giấy điệp nổi tiếng:',
    hint: 'Dòng tranh dân gian truyền thống gắn với vùng quê Kinh Bắc',
    answer: 'ĐÔNG HỒ',
    imageUrl: '/images/artifacts/tranh-dan-gian/007-dau-vat-5aed78c78c20a00026459a33.webp',
    imageAlt: 'Tranh Đấu vật Đông Hồ',
    imageCaption: 'Tranh dân gian Đông Hồ · Đấu vật (In mộc bản trên giấy điệp)',
    imageSpoilsAnswer: true,
    explanation: 'Tranh dân gian Đông Hồ (Thuận Thành, Bắc Ninh) in bằng các bản khắc gỗ với màu sắc tự nhiên lấy từ tro lá tre, hoa hòe, than xoan và bột sò điệp lấp lánh.'
  },
  {
    id: 'q10',
    type: 'choice',
    label: 'TRẮC NGHIỆM · TẦNG 10',
    text: 'Kiệt tác “Bác Hồ ở chiến khu Việt Bắc” do danh họa nào trong nhóm “Nghiêm - Liên - Sáng - Phái” sáng tác?',
    choices: ['Dương Bích Liên', 'Nguyễn Tư Nghiêm', 'Nguyễn Sáng', 'Bùi Xuân Phái'],
    correct: 0,
    imageUrl: '/images/artifacts/bac-ho-o-chien-khu-viet-bac-duong-bich-lien.webp',
    imageAlt: 'Bác Hồ ở chiến khu Việt Bắc của Dương Bích Liên',
    imageCaption: 'Bác Hồ ở chiến khu Việt Bắc · Dương Bích Liên (Bảo vật Quốc gia, Sơn mài 1980)',
    imageSpoilsAnswer: true,
    explanation: 'Dương Bích Liên là học trò xuất sắc của trường Mỹ thuật Đông Dương, từng gắn bó trực tiếp với chiến khu Việt Bắc bên cạnh Chủ tịch Hồ Chí Minh.'
  },
  {
    id: 'q11',
    type: 'choice',
    label: 'TRẮC NGHIỆM · TẦNG 11',
    text: 'Cặp “Cánh cửa chạm rồng” thế kỷ 17 lưu giữ tại Bảo tàng là hiện vật nguyên gốc thuộc ngôi chùa cổ nào?',
    choices: ['Chùa Keo (Thái Bình)', 'Chùa Thầy (Hà Nội)', 'Chùa Dâu (Bắc Ninh)', 'Chùa Bút Tháp (Bắc Ninh)'],
    correct: 0,
    imageUrl: '/images/artifacts/canh-cua-cham-rong.webp',
    imageAlt: 'Cánh cửa chạm rồng chùa Keo',
    imageCaption: 'Cánh cửa chạm rồng · Chùa Keo (Bảo vật Quốc gia, Điêu khắc gỗ thế kỷ 17)',
    imageSpoilsAnswer: false,
    explanation: 'Cặp cánh cửa chùa Keo là kiệt tác điêu khắc gỗ thời Lê Trung Hưng với nghệ thuật chạm lộng nhiều tầng lớp hình rồng và mây lửa cuồn cuộn uy linh.'
  },
  {
    id: 'q12',
    type: 'match',
    label: 'NỐI TÁC PHẨM & THỂ LOẠI · TẦNG 12',
    text: 'Nối từng kiệt tác với thể loại nghệ thuật tương ứng:',
    rightTitle: 'THỂ LOẠI',
    imageUrl: '/images/artifacts/phat-ba-quan-am.webp',
    imageCaption: 'Đa dạng các thể loại trong kho tàng Mỹ thuật Việt Nam',
    imageSpoilsAnswer: false,
    pairs: [
      { left: 'Em Thúy', right: 'Tranh chân dung', image: '/images/artifacts/em-thuy-tran-van-can.webp' },
      { left: 'Phật bà Quan Âm', right: 'Tượng điêu khắc gỗ', image: '/images/artifacts/phat-ba-quan-am.webp' },
      { left: 'Gióng', right: 'Tranh biểu tượng lịch sử', image: '/images/artifacts/giong-nguyen-tu-nghiem.webp' }
    ],
    explanation: 'Mỗi thể loại nghệ thuật mang lại một phương thức biểu đạt cảm xúc và câu chuyện văn hóa độc đáo qua các thời kỳ lịch sử dân tộc.'
  }
];

export interface LuckyWheelPrize {
  id: number;
  name: string;
  image: string;
  hearts: number;
  isStamp?: boolean;
  color: string;
  desc: string;
}

export const LUCKY_WHEEL_PRIZES: LuckyWheelPrize[] = [
  {
    id: 0,
    name: 'Hồi 1 Tim',
    image: '/reward-heart.svg',
    hearts: 1,
    color: '#a50021',
    desc: 'Hồi phục ngay +1 sinh lực để tiếp tục hành trình leo tháp!',
  },
  {
    id: 1,
    name: 'Hồi 2 Tim',
    image: '/reward-hearts.svg',
    hearts: 2,
    color: '#e6ba55',
    desc: 'Đại cát! Bạn được hồi phục +2 sinh lực, tăng cường sức bền lên đỉnh tháp!',
  },
  {
    id: 2,
    name: 'Chúc may mắn',
    image: '/reward-empty.svg',
    hearts: 0,
    color: '#7d6141',
    desc: 'Lần này chưa hồi tim, nhưng trí tuệ và sự kiên định sẽ giúp bạn vượt qua!',
  },
  {
    id: 3,
    name: 'Tem Gióng Mẫu',
    image: '/images/generated/stamps-v2/web/stamp-giong-v2.webp',
    hearts: 0,
    isStamp: true,
    color: '#4caf82',
    desc: 'Chúc mừng bạn đã quay trúng Tem di sản Gióng - kiệt tác danh họa Nguyễn Tư Nghiêm!',
  },
];
